import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { RootStatePlatform } from "../../store/platform-slice";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import SecondaryTabMenuPicker from "../common/SecondaryTabMenuPicker";
import BodyMapTab from "./BodyMapTab";
import RecommendedServicesTab from "./RecommendedServicesTab";
import { useScoreSidePanel } from "./ScoreSidePanelContext";
import { BodyCompositionFigure } from "../../../consultation/components/section2-systems/BodyCompositionView";
import { normalizeBodyComposition } from "../../../consultation/data/fetchConsultationPrep";
import { useAppStore } from "../../../../providers/AppStore";
import { useAuth } from "../../../../providers/AuthProvider";
import { useSubject } from "../../providers/SubjectProvider";
import { usePlatformResource } from "../../hooks/usePlatformResource";

// System id 26 is the synthetic Body Composition system the backend appends
// to `score.systems[]` for the consultation work. When selected we swap the
// organ-figure body map for the consultation's segmental body figure (same
// component used in Section 2 of the consultation), backed by the real
// `GetConsultation` endpoint.
const BODY_COMPOSITION_SYSTEM_ID = 26;

const BodyMapAndServicesPanel = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const { setHighlightedBiomarkerIds } = useScoreSidePanel();
  const { subjectId } = useSubject();
  const { AuthUser } = useAuth();
  const { ASConsultation } = useAppStore();

  const selectedSystem = useSelector(
    (state: RootStatePlatform) => state.platform.selectedSystem
  );

  // Real consultation-prep body comp data, from the shared "consultation"
  // resource (one `GetConsultation` per subject, also read by the Energy &
  // Training Zones system). Loaded even if the user never selects system 26:
  // the payload is small and avoids a click-delay when they do. A failed load
  // is non-fatal — body comp simply won't render; other systems still work.
  usePlatformResource("consultation", subjectId, AuthUser?.role);
  const consultation = ASConsultation(subjectId);
  const bodyComposition = useMemo(
    () =>
      consultation
        ? normalizeBodyComposition(consultation.bodyComposition)
        : null,
    [consultation]
  );

  useEffect(() => {
    if (selectedSystem === 0) {
      setActiveTab(0);
    }
  }, [selectedSystem]);

  useEffect(() => {
    if (activeTab === 0) {
      setHighlightedBiomarkerIds(new Set());
    }
  }, [activeTab]);

  const isBodyComposition = selectedSystem === BODY_COMPOSITION_SYSTEM_ID;

  // Pick which body-figure column to render. Recommended services tab wins
  // over both — it's user-toggled. Body composition only kicks in on the
  // Body map tab.
  const renderBodyMapColumn = () => {
    if (activeTab === 1) return <RecommendedServicesTab />;
    if (isBodyComposition)
      return <BodyCompositionFigure bodyComposition={bodyComposition} />;
    return <BodyMapTab />;
  };

  return (
    <div
      style={styles.imgContainer(theme, activeTab === 1, selectedSystem !== 0)}
    >
      {selectedSystem !== 0 && (
        <div style={styles.tabWrapper}>
          <SecondaryTabMenuPicker
            tabs={[
              { label: "Body map", key: 0 },
              { label: "Recommended services", key: 1 },
            ]}
            selectedTab={activeTab}
            setSelectedTab={setActiveTab}
            tabStyle={{ flex: 1, justifyContent: "center" }}
          />
        </div>
      )}
      {renderBodyMapColumn()}
    </div>
  );
};

export default BodyMapAndServicesPanel;

const styles = {
  imgContainer: (
    theme: Theme,
    isServiceTabActive: boolean,
    hasSelectedSystem: boolean
  ) => ({
    backgroundColor: isServiceTabActive
      ? theme.colors_ds.NEUTRAL_BG_MAIN
      : theme.colorTokens.N[30],
    border: `1px solid ${
      isServiceTabActive
        ? theme.colors_ds.NEUTRAL_BORDER_WEAK
        : theme.colorTokens.N[20]
    }`,
    maxWidth: hasSelectedSystem ? "535px" : "",
    flex: 1,
    display: "flex",
    borderRadius: "8px",
    flexDirection: "column" as "column",
    flexGrow: 1,
    minHeight: 0,
    position: "relative" as "relative",
  }),
  tabWrapper: {
    padding: "8px 16px 0",
  },
};
